import React, { useRef, useState, useEffect } from "react";
import "../styles/GalleryStyles.css";
import { useApi } from "../../../service/ApiContext";
import BottomOverlay from "../../dashboard/components/BottomOverlay";
import axios from "axios";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";

interface Props {
  onSubmit: (file: File, s3Url?: string) => void;
  mediaType: string;
  disabled?: boolean;
  program_id?: string;
  subject_id?: string;
  topic_id?: string;
  video_id?: string;
  program_name?: string;
  video_name?: string;
  subject_name?: string;
  topic_name?: string;
}

type ResolutionType = "VGA (640x480)" | "HD (1280x720)" | "FHD (1920x1080)";

const ImageCapture: React.FC<Props> = ({
  onSubmit,
  mediaType,
  disabled = false,
  program_id,
  subject_id,
  topic_id,
  video_id,
  program_name,
  video_name,
  subject_name,
  topic_name,
}) => {
  const api = useApi();
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const [fileName, setFileName] = useState("");
  const [fileNameError, setFileNameError] = useState("");
  // Refs to hold current values for unmount cleanup
  const activeStreamRef = useRef<MediaStream | null>(null);
  const previewRef = useRef<string | null>(null);
  const programs = useSelector(
    (state: RootState) => state.events.selectedValues,
  );
  const selectedProgramId = programs?.[0]?.program_id || "";
  const selectedProgramName = programs?.[0]?.program_title || "";
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [permissionGranted, setPermissionGranted] = useState<boolean>(false);
  const [permissionError, setPermissionError] = useState<string | null>(null);
  const [loadingCamera, setLoadingCamera] = useState<boolean>(false);

  const [preview, setPreview] = useState<string | null>(null);
  const [capturedBlob, setCapturedBlob] = useState<Blob | null>(null);

  // Custom Resolution configuration
  const [resolution, setResolution] = useState<ResolutionType>("HD (1280x720)");

  // Countdown states
  const [countdown, setCountdown] = useState<number | null>(null);

  // simulated S3 upload states
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<boolean>(false);

  //   // Parse width & height based on selection
  //   const getResolutionDimensions = (res: ResolutionType) => {
  //     switch (res) {
  //       case "VGA (640x480)":
  //         return { width: 640, height: 480 };
  //       case "FHD (1920x1080)":
  //         return { width: 1920, height: 1080 };
  //       case "HD (1280x720)":
  //       default:
  //         return { width: 1280, height: 720 };
  //     }
  //   };

  const startCamera = async (resType: ResolutionType = resolution) => {
    setLoadingCamera(true);
    setPermissionError(null);

    // Stop any existing tracks before starting new ones
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
    }

    const width = 1280;
    const height = 720;
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: width },
          height: { ideal: height },
          facingMode: "user",
        },
        audio: false, // only capture camera for image
      });

      setStream(mediaStream);
      setPermissionGranted(true);

      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (error: any) {
      setPermissionGranted(false);
      setPermissionError(
        error.name === "NotAllowedError"
          ? "Permission denied. Please grant camera access in your browser settings."
          : "Could not access camera. Please check if another app is using it.",
      );
    } finally {
      setLoadingCamera(false);
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  // Switch resolution on the fly
  //   const handleResolutionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
  //     const newRes = e.target.value as ResolutionType;
  //     setResolution(newRes);
  //     if (permissionGranted && !preview) {
  //       startCamera(newRes);
  //     }
  //   };

  // Synth camera shutter audio feedback
  const playShutterSound = () => {
    try {
      const ctx = new (
        window.AudioContext || (window as any).webkitAudioContext
      )();

      // white noise burst for shutter sound
      const bufferSize = ctx.sampleRate * 0.1; // 100ms
      const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) {
        data[i] = Math.random() * 2 - 1;
      }

      const noise = ctx.createBufferSource();
      noise.buffer = buffer;

      const filter = ctx.createBiquadFilter();
      filter.type = "bandpass";
      filter.frequency.value = 1000;

      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.6, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.08);

      noise.connect(filter);
      filter.connect(gain);
      gain.connect(ctx.destination);

      noise.start();
    } catch (e) {}
  };

  const captureImageWithTimer = () => {
    if (!videoRef.current || countdown !== null) return;

    // Start 3-second countdown
    setCountdown(3);
    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev === null) {
          clearInterval(interval);
          return null;
        }
        if (prev <= 1) {
          clearInterval(interval);
          // Action time! Take the photo
          setTimeout(() => {
            executeCapture();
            setCountdown(null);
          }, 600); // slight delay to show "Action!"
          return 0; // indicates Action state
        }
        return prev - 1;
      });
    }, 1000);
  };

  const executeCapture = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    // Use current stream dimensions
    const width = video.videoWidth || 1280;
    const height = video.videoHeight || 720;

    canvas.width = width;
    canvas.height = height;

    if (ctx) {
      // Mirror image horizontally to match webcam display preview
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      // Reset transform
      ctx.setTransform(1, 0, 0, 1, 0, 0);
    }

    playShutterSound();

    // Export as webp for compression
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        setPreview(url);
        setCapturedBlob(blob);
        stopCamera();
      },
      "image/webp",
      0.9, // 90% compression quality
    );
  };

  const handleRecapture = () => {
    if (preview) {
      URL.revokeObjectURL(preview);
    }
    setPreview(null);
    setCapturedBlob(null);
    startCamera(resolution);
  };

  const submit = async () => {
    if (!capturedBlob) return;

    setUploadProgress(0);
    setUploadSuccess(false);

    try {
      const file = new File([capturedBlob], `image-${Date.now()}.webp`, {
        type: "image/webp",
      });

      // STEP 1: Get Presigned URL
      const presignedRes = await api.get<{
        uploadURL: string;
        key: string;
        downloadURL: string;
      }>(`/create_upload_url?file_type=${mediaType}`);

      const { uploadURL, downloadURL } = presignedRes.data;

      // STEP 2: Upload directly to S3
      await axios.put(uploadURL, file, {
        headers: {
          "Content-Type": file.type,
        },
        onUploadProgress: (progressEvent: any) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / (progressEvent.total || 1),
          );

          setUploadProgress(Math.min(99, percentCompleted));
        },
      });

      // STEP 3: Notify backend
      const response = await api.post("/gallery_upload", {
        url: downloadURL,
        file_type: mediaType,
        program_id: program_id || selectedProgramId || "",
        subject_id: subject_id || "",
        topic_id: topic_id || "",
        video_id: video_id || "",
        program_name: program_name || selectedProgramName || "",
        video_name: video_name || "",
        subject_name: subject_name || "",
        topic_name: topic_name || "",
      });

      setUploadProgress(100);
      setUploadSuccess(true);

      setTimeout(() => {
        onSubmit(file, downloadURL);

        setPreview(null);
        setCapturedBlob(null);
        setUploadProgress(null);
        setUploadSuccess(false);
      }, 1200);
    } catch (error) {
      setUploadProgress(null);
      setUploadSuccess(false);
    }
  };

  // const submit = async () => {
  //   if (!capturedBlob) return;

  //   setUploadProgress(0);
  //   setUploadSuccess(false);

  //   try {
  //     const file = new File([capturedBlob], `image-${Date.now()}.webp`, {
  //       type: "image/webp",
  //     });

  //     const formData = new FormData();
  //     formData.append("file", file);

  //     // Call FastAPI gallery_upload endpoint with format query param and multipart body
  //     const response = await api.post<any>(
  //       `/gallery_upload?file_type=${mediaType}`,
  //       formData,
  //       {
  //         headers: {
  //           "Content-Type": "multipart/form-data",
  //         },
  //         onUploadProgress: (progressEvent: any) => {
  //           const percentCompleted = Math.round(
  //             (progressEvent.loaded * 100) / progressEvent.total,
  //           );
  //           setUploadProgress(Math.min(99, percentCompleted));
  //         },
  //       },
  //     );

  //     setUploadProgress(100);
  //     setUploadSuccess(true);

  //     const s3Url =
  //       response.data?.url ||
  //       response.data?.s3_url ||
  //       response.data?.data?.url ||
  //       "";

  //     // Show success animation briefly before passing to parent
  //     setTimeout(() => {
  //       onSubmit(file, s3Url);
  //       // Reset states
  //       setPreview(null);
  //       setCapturedBlob(null);
  //       setUploadProgress(null);
  //       setUploadSuccess(false);
  //     }, 1200);
  //   } catch (error) {
  //     setUploadProgress(null);
  //     setUploadSuccess(false);
  //   }
  // };

  // Keep refs up-to-date
  useEffect(() => {
    activeStreamRef.current = stream;
  }, [stream]);

  useEffect(() => {
    previewRef.current = preview;
  }, [preview]);

  // Clean up on unmount only
  useEffect(() => {
    return () => {
      if (activeStreamRef.current) {
        activeStreamRef.current.getTracks().forEach((track) => track.stop());
      }
      if (previewRef.current) {
        URL.revokeObjectURL(previewRef.current);
      }
    };
  }, []);

  // Bind live stream to video element when ready
  useEffect(() => {
    if (videoRef.current && stream && !preview) {
      videoRef.current.srcObject = stream;
    }
  }, [stream, preview]);

  // Render permission screen if not granted
  if (!permissionGranted) {
    return (
      <div className="permission_card">
        <div className="permission_icon_container">📷</div>
        <h3>Camera Permission Required</h3>
        <p>
          To capture Media and preview hands-on tasks, Jozuna needs permission
          to access your device's camera.
        </p>
        {permissionError && (
          <div
            style={{
              color: "#ef4444",
              fontSize: "13px",
              marginBottom: "16px",
              fontWeight: 500,
            }}
          >
            {permissionError}
          </div>
        )}
        <button
          className="primary_btn"
          onClick={() => startCamera(resolution)}
          disabled={loadingCamera}
        >
          {loadingCamera ? "Requesting Access..." : "Grant Camera Access"}
        </button>
      </div>
    );
  }

  return (
    <div className="capture_section">
      <div className="capture_card">
        <div className="capture_header">
          <h3>Capture Image</h3>
          <p>
            Frame your hands-on Media clearly. The image will be compressed
            automatically in WebP format.
          </p>
        </div>

        <div className="camera_container">
          {/* Active webcam stream */}
          {!preview && (
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="camera_preview"
            />
          )}

          {/* Picture preview after capture */}
          {preview && (
            <img
              src={preview}
              alt="Preview"
              className="captured_preview no-mirror"
            />
          )}

          {/* Countdown timer overlay */}
          {countdown !== null && (
            <div className="camera_overlay_countdown">
              {countdown > 0 ? (
                <div className="countdown_number">{countdown}</div>
              ) : (
                <div className="countdown_text">Action! 📸</div>
              )}
            </div>
          )}

          {/* S3 Upload Progress overlay */}
          {uploadProgress !== null && (
            <div className="camera_overlay_uploading">
              {!uploadSuccess ? (
                <>
                  <div className="spinner"></div>
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: "16px",
                      letterSpacing: "0.2px",
                    }}
                  >
                    Uploading... ({uploadProgress}%)
                  </div>
                  {/* <div style={{ fontSize: "12px", opacity: 0.7 }}>
                    Generating pre-signed URL & storing image safely
                  </div> */}
                </>
              ) : (
                <>
                  <div className="upload_success_checkmark">✓</div>
                  <div
                    style={{
                      fontWeight: 700,
                      fontSize: "18px",
                      color: "#10b981",
                    }}
                  >
                    Uploaded Successfully!
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Action Controls Row */}
        <div className="capture_controls_row">
          <div className="capture_actions">
            {!preview ? (
              <button
                className="primary_btn"
                onClick={captureImageWithTimer}
                disabled={disabled || countdown !== null || uploadProgress !== null}
              >
                {countdown !== null ? "Get Ready..." : "Capture Image"}
              </button>
            ) : (
              <>
                <button
                  className="secondary_btn"
                  onClick={handleRecapture}
                  disabled={uploadProgress !== null}
                >
                  Recapture
                </button>
                <button
                  className="success_btn"
                  onClick={() => {
                    submit();
                  }}
                  disabled={uploadProgress !== null}
                >
                  Submit to Gallery
                </button>
              </>
            )}
          </div>
        </div>

        <canvas ref={canvasRef} style={{ display: "none" }} />
      </div>
      {/* {isOverlayOpen && (
        <div
          onClick={() => setIsOverlayOpen(false)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(15, 23, 42, 0.55)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 99999,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              maxWidth: "500px",
              background: "#fff",
              borderRadius: "16px",
              padding: "24px",
              boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
            }}
          >
            <div style={{ marginBottom: "20px" }}>
              <h3
                style={{
                  margin: 0,
                  fontSize: "22px",
                  fontWeight: 600,
                  color: "#0f172a",
                }}
              >
                Upload to Gallery
              </h3>

              <p
                style={{
                  marginTop: "8px",
                  color: "#64748b",
                  fontSize: "14px",
                }}
              >
                Enter a name for your image before uploading it to the gallery.
              </p>
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
              }}
            >
              <label
                style={{
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#334155",
                }}
              >
                Image Name
              </label>

              <input
                type="text"
                value={fileName}
                onChange={(e) => {
                  setFileName(e.target.value);

                  if (e.target.value.trim()) {
                    setFileNameError("");
                  }
                }}
                placeholder="Enter image name"
                style={{
                  width: "100%",
                  height: "48px",
                  border: fileNameError
                    ? "1px solid #ef4444"
                    : "1px solid #cbd5e1",
                  borderRadius: "10px",
                  padding: "0 14px",
                  fontSize: "14px",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
              {fileNameError && (
                <div
                  style={{
                    color: "#ef4444",
                    fontSize: "12px",
                    marginTop: "6px",
                    fontWeight: 500,
                  }}
                >
                  {fileNameError}
                </div>
              )}
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "12px",
                marginTop: "24px",
              }}
            >
              <button
                onClick={() => {
                  setFileName("");
                  setIsOverlayOpen(false);
                }}
                style={{
                  minWidth: "100px",
                  height: "42px",
                  border: "1px solid #cbd5e1",
                  borderRadius: "8px",
                  background: "#fff",
                  color: "#475569",
                  cursor: "pointer",
                  fontWeight: 500,
                }}
              >
                Cancel
              </button>

              <button
                disabled={!fileName.trim()}
                onClick={() => {
                  if (!fileName.trim()) {
                    setFileNameError("Image name is required");
                    return;
                  }

                  setFileNameError("");
                  setIsOverlayOpen(false);

                  submit();
                }}
                style={{
                  minWidth: "120px",
                  height: "42px",
                  border: "none",
                  borderRadius: "8px",
                  background: !fileName.trim() ? "#cbd5e1" : "#173f8a",
                  color: "#fff",
                  cursor: !fileName.trim() ? "not-allowed" : "pointer",
                  fontWeight: 600,
                  opacity: !fileName.trim() ? 0.8 : 1,
                }}
              >
                Upload
              </button>
            </div>
          </div>
        </div>
      )} */}
    </div>
  );
};

export default ImageCapture;
